/**
 * Performance (`/performance`) — the active site's cross-plane
 * performance overview. Default landing surface after the 2026-05-30
 * IA split (Performance / Traffic / Behaviours / Sales).
 *
 * The three data planes each have their own route now — promoted from
 * this route's old `?view=` tab bar to the sidebar section panel
 * (SidebarSubmenu) on 2026-07-26:
 *   - `/performance/real`   — pixel RUM (`PerformanceSection`).
 *   - `/performance/field`  — Chrome UX Report (`CruxSection` full).
 *   - `/performance/audits` — synthetic Lighthouse (`LabPerformance`).
 *
 * This route keeps only the **Overview**: a control-free origin
 * summary that stacks three CWV tile-chip cards (real-user pixel /
 * CrUX field / synthetic audit p75), each deep-linking into its full
 * route — via the card header, or by clicking a single tile, which
 * lands there with the vital already picked in its metric filter
 * (`?metric=`). A plane with no data yet shows its empty "—" tile
 * shells behind a subtle gradient cover with a minimal nudge.
 *
 * Legacy `?view=` URLs (pre-promotion tab links, still baked into the
 * marketing-site demo embeds) redirect to the plane routes in
 * `beforeLoad`, carrying `sub` / `metric` / `compare` along.
 */
import { DEFAULT_PAGE_RANGE } from '@/components/primitives/PageRangePill';
import { SectionBoundary } from '@/components/primitives/SectionBoundary';
import {
  CHART_METRIC_KEYS,
  CWV_METRIC_KEYS,
  type ChartMetricKey,
  type MetricKey,
  OverviewSection,
} from '@/components/rum/PerformanceSection';
import { SiteIsland } from '@/components/shell/SiteIsland';
import { useActiveSiteUuid } from '@/lib/active-site';
import { useSiteCrux } from '@/lib/queries/crux';
import { useSitePixelHealth, useSitePixelPerf, useSitePixelPerfTrend } from '@/lib/queries/pixel';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactElement } from 'react';

/** Legacy `?view=` values from when the planes were tabs on this
 *  route. Accepted only to redirect; never rendered here. */
type LegacyPerfView = 'overview' | 'real' | 'audits' | 'field' | 'thirdparty';
type LegacyAuditSub = 'scores' | 'thirdparty' | 'pages';

interface PerfSearch {
  view?: LegacyPerfView;
  sub?: LegacyAuditSub;
  compare?: string;
  metric?: ChartMetricKey;
}

const isChartMetricKey = (value: unknown): value is ChartMetricKey =>
  CHART_METRIC_KEYS.includes(value as ChartMetricKey);
const isMetricKey = (value: unknown): value is MetricKey =>
  CWV_METRIC_KEYS.includes(value as MetricKey);

export const Route = createFileRoute('/_app/performance/')({
  // The legacy params survive validation solely so `beforeLoad` can
  // translate them — plain `to="/performance"` links carry none.
  validateSearch: (search: Record<string, unknown>): PerfSearch => ({
    ...(search.view === 'overview' ||
    search.view === 'audits' ||
    search.view === 'real' ||
    search.view === 'field' ||
    search.view === 'thirdparty'
      ? { view: search.view }
      : {}),
    ...(search.sub === 'scores' || search.sub === 'thirdparty' || search.sub === 'pages'
      ? { sub: search.sub }
      : {}),
    ...(typeof search.compare === 'string' && search.compare !== ''
      ? { compare: search.compare }
      : {}),
    ...(isChartMetricKey(search.metric) ? { metric: search.metric } : {}),
  }),
  // Legacy `?view=` tab links → the promoted plane routes. `replace`
  // so Back doesn't bounce through the redirect.
  beforeLoad: ({ search }) => {
    if (!search.view) return;
    if (search.view === 'overview') {
      throw redirect({ to: '/performance', search: {}, replace: true });
    }
    if (search.view === 'real' || search.view === 'field') {
      throw redirect({
        to: search.view === 'real' ? '/performance/real' : '/performance/field',
        search: {
          // Real users + Field data can only draw the five vitals — a
          // Lighthouse category score in a legacy link is dropped.
          ...(isMetricKey(search.metric) ? { metric: search.metric } : {}),
          ...(search.view === 'real' && search.compare ? { compare: search.compare } : {}),
        },
        replace: true,
      });
    }
    // Third parties is its own route now — both the `?view=thirdparty`
    // alias and `?view=audits&sub=thirdparty` land there directly rather
    // than bouncing through the Audits route's own redirect. www's demo
    // iframes still embed the latter, so this hop has to stay exact.
    if (search.view === 'thirdparty' || search.sub === 'thirdparty') {
      throw redirect({
        to: '/performance/third-parties',
        search: search.compare ? { compare: search.compare } : {},
        replace: true,
      });
    }
    // `audits`. `sub=pages` is gone — the Origin / Pages split collapsed
    // into one view whose zoom is `?page=`, and a legacy link has no page
    // uuid to carry, so it lands on the origin.
    throw redirect({
      to: '/performance/audits',
      search: {
        ...(search.metric ? { metric: search.metric } : {}),
        ...(search.compare ? { compare: search.compare } : {}),
      },
      replace: true,
    });
  },
  component: PerformancePage,
});

function PerformancePage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  return (
    <SiteIsland section="Performance">
      {siteUuid && <OverviewBody siteUuid={siteUuid} />}
    </SiteIsland>
  );
}

function OverviewBody({ siteUuid }: { siteUuid: string }): ReactElement {
  const navigate = Route.useNavigate();
  // The Overview is deliberately control-free (no range pill) — every
  // card summarises the default window; the plane routes own exploration.
  const range = DEFAULT_PAGE_RANGE;

  // Pixel install signal — drives the real-user card's setup cover.
  const health = useSitePixelHealth(siteUuid);
  const beaconsToday = health.data?.beacons_today ?? 0;
  // `last_beacon_at` is the actual "we've seen a beacon at some point"
  // signal — the healthToday stub returns `1970-01-01 …` when there's
  // no row, so `health.data !== undefined` is always true and would
  // claim "snippet is installed" on sites that never installed one.
  const lastBeaconAt = health.data?.last_beacon_at;
  const everSeenBeacon = !!lastBeaconAt && !lastBeaconAt.startsWith('1970');
  const rumConnected = beaconsToday > 0 || everSeenBeacon;

  // CrUX ALL series — the field-summary card. One hour-cached call.
  const cruxAll = useSiteCrux(siteUuid, { formFactor: 'ALL' });

  // Real-user card pipes — headline p75s + daily sparkline, always
  // all-audience (the overview has no filter controls). These share
  // query keys with the Real users route's unfiltered pipes, so
  // hopping between Overview and an unfiltered `/performance/real`
  // reuses cache.
  const overviewPerf = useSitePixelPerf(siteUuid, { days: range });
  const overviewTrend = useSitePixelPerfTrend(siteUuid, { days: range });

  // Tile click-through: open the plane's full route with the clicked
  // vital pre-selected. A real navigation so Back returns here.
  const openMetric = (view: 'real' | 'field' | 'audits', metric: MetricKey): void => {
    void navigate({
      to:
        view === 'real'
          ? '/performance/real'
          : view === 'field'
            ? '/performance/field'
            : '/performance/audits',
      search: { metric },
    });
  };

  return (
    <SectionBoundary label="this view">
      <OverviewSection
        siteUuid={siteUuid}
        range={range}
        perf={overviewPerf.data}
        trend={overviewTrend.data}
        perfLoading={overviewPerf.isLoading || overviewTrend.isLoading}
        perfSettled={overviewPerf.isSuccess}
        rumConnected={rumConnected}
        crux={cruxAll.data}
        onOpenMetric={openMetric}
      />
    </SectionBoundary>
  );
}
